import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Bell, Pin, X, Plus } from 'lucide-react';
import { TYPO, S, R, SHADCN, softTone } from '../theme';

// ── <NotifBell> — espace notifications du dashboard ─────────────────────────
// Cloche dans la TopBar : nouveautés / infos / alertes publiées par les admins.
// audience 'tous' = tout le monde, 'admin' = admin + cafe_admin (le serveur
// filtre — le client n'a jamais les annonces admin s'il n'a pas le rôle).
// Lu / non-lu : localStorage uniquement (annonces_lues_v1) — pas de table de
// lecture, un badge par appareil suffit pour une équipe de cette taille.

const LUES_KEY = 'annonces_lues_v1';

const lireLues = () => {
  try { return new Set(JSON.parse(localStorage.getItem(LUES_KEY) || '[]')); }
  catch { return new Set(); }
};
const sauverLues = (set) => {
  try { localStorage.setItem(LUES_KEY, JSON.stringify([...set].slice(-200))); }
  catch { /* stockage plein/privé : le badge revivra, pas grave */ }
};

const TYPE_META = {
  nouveaute: { label: 'Nouveauté', tone: () => softTone(SHADCN.success) },
  alerte:    { label: 'Alerte',    tone: () => softTone(SHADCN.warning) },
  info:      { label: 'Info',      tone: () => softTone(SHADCN.mutedFg) },
};

const fmtDate = (iso) => {
  try {
    return new Date(iso).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
  } catch { return ''; }
};

// Formulaire de publication (admins) — replié derrière un bouton "+".
function FormAnnonce({ onPublie }) {
  const [ouvert, setOuvert]     = useState(false);
  const [titre, setTitre]       = useState('');
  const [corps, setCorps]       = useState('');
  const [type, setType]         = useState('info');
  const [audience, setAudience] = useState('tous');
  const [envoi, setEnvoi]       = useState(false);

  const inputStyle = {
    width: '100%', boxSizing: 'border-box',
    background: SHADCN.bg, color: SHADCN.fg,
    border: `1px solid ${SHADCN.input}`, borderRadius: R.sm,
    padding: `${S[2]}px ${S[2]}px`, ...TYPO.body, outline: 'none',
  };

  const publier = async () => {
    if (!titre.trim() || !corps.trim() || envoi) return;
    setEnvoi(true);
    try {
      const res = await fetch('/api/supabase-data?resource=annonces', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ titre: titre.trim(), corps: corps.trim(), type, audience }),
      });
      const json = await res.json();
      if (json.ok) {
        setTitre(''); setCorps(''); setType('info'); setAudience('tous'); setOuvert(false);
        onPublie();
      }
    } finally { setEnvoi(false); }
  };

  if (!ouvert) {
    return (
      <button onClick={() => setOuvert(true)} style={{
        display: 'flex', alignItems: 'center', gap: S[1], width: '100%',
        background: 'none', border: `1px dashed ${SHADCN.border}`, borderRadius: R.sm,
        color: SHADCN.mutedFg, cursor: 'pointer', padding: `${S[2]}px ${S[2]}px`, ...TYPO.caption,
      }}>
        <Plus size={13} /> Nouvelle annonce
      </button>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: S[2], border: `1px solid ${SHADCN.border}`, borderRadius: R.sm, padding: S[2] }}>
      <input style={inputStyle} placeholder="Titre" value={titre} onChange={e => setTitre(e.target.value)} maxLength={200} />
      <textarea style={{ ...inputStyle, minHeight: 72, resize: 'vertical' }} placeholder="Message…" value={corps} onChange={e => setCorps(e.target.value)} maxLength={4000} />
      <div style={{ display: 'flex', gap: S[2] }}>
        <select style={{ ...inputStyle, flex: 1 }} value={type} onChange={e => setType(e.target.value)}>
          <option value="info">Info</option>
          <option value="nouveaute">Nouveauté</option>
          <option value="alerte">Alerte</option>
        </select>
        <select style={{ ...inputStyle, flex: 1 }} value={audience} onChange={e => setAudience(e.target.value)}>
          <option value="tous">Visible par tous</option>
          <option value="admin">Admins uniquement</option>
        </select>
      </div>
      <div style={{ display: 'flex', gap: S[2], justifyContent: 'flex-end' }}>
        <button onClick={() => setOuvert(false)} style={{ background: 'none', border: 'none', color: SHADCN.mutedFg, cursor: 'pointer', ...TYPO.caption }}>Annuler</button>
        <button onClick={publier} disabled={envoi || !titre.trim() || !corps.trim()} style={{
          background: SHADCN.primary, color: SHADCN.primaryFg, border: 'none', borderRadius: R.sm,
          padding: `${S[1]}px ${S[3]}px`, cursor: 'pointer', ...TYPO.caption, fontWeight: 600,
          opacity: envoi || !titre.trim() || !corps.trim() ? 0.5 : 1,
        }}>
          {envoi ? 'Publication…' : 'Publier'}
        </button>
      </div>
    </div>
  );
}

export default function NotifBell({ role, isMobile }) {
  const [annonces, setAnnonces] = useState([]);
  const [ouvert, setOuvert]     = useState(false);
  const [lues, setLues]         = useState(lireLues);
  const boxRef = useRef(null);
  const isAdmin = role === 'admin' || role === 'cafe_admin';

  const charger = useCallback(async () => {
    try {
      const res  = await fetch('/api/supabase-data?resource=annonces');
      const json = await res.json();
      if (Array.isArray(json.annonces)) setAnnonces(json.annonces);
    } catch { /* silencieux — la cloche reste sans badge */ }
  }, []);

  useEffect(() => {
    charger();
    const timer = setInterval(charger, 30 * 60 * 1000);
    return () => clearInterval(timer);
  }, [charger]);

  // Clic hors du panneau → fermeture
  useEffect(() => {
    if (!ouvert) return;
    const onDown = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOuvert(false); };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [ouvert]);

  const nonLues = annonces.filter(a => !lues.has(a.id)).length;

  const ouvrir = () => {
    const next = !ouvert;
    setOuvert(next);
    if (next && nonLues) {
      // Ouvrir le panneau marque tout comme lu — le badge signale du nouveau,
      // pas une todo-list : une fois vu, il disparaît.
      const s = new Set(lues);
      annonces.forEach(a => s.add(a.id));
      setLues(s); sauverLues(s);
    }
  };

  const supprimer = async (id) => {
    await fetch(`/api/supabase-data?resource=annonces&id=${id}`, { method: 'DELETE' }).catch(() => {});
    setAnnonces(prev => prev.filter(a => a.id !== id));
  };

  return (
    <div ref={boxRef} style={{ position: 'relative', flexShrink: 0 }}>
      <button onClick={ouvrir} aria-label="Notifications" title="Notifications" style={{
        position: 'relative', background: 'none', border: 'none', cursor: 'pointer',
        color: ouvert ? SHADCN.fg : SHADCN.mutedFg, padding: `${S[1]}px ${S[2]}px`,
        display: 'flex', alignItems: 'center',
      }}>
        <Bell size={16} strokeWidth={1.7} />
        {nonLues > 0 && (
          <span style={{
            position: 'absolute', top: 0, right: 2,
            minWidth: 14, height: 14, borderRadius: R.pill,
            background: SHADCN.destructive, color: SHADCN.destructiveFg,
            fontSize: 9, fontWeight: 700, lineHeight: '14px', textAlign: 'center',
            padding: '0 3px', boxSizing: 'border-box',
          }}>
            {nonLues > 9 ? '9+' : nonLues}
          </span>
        )}
      </button>

      {ouvert && (
        <div style={{
          position: isMobile ? 'fixed' : 'absolute',
          top: isMobile ? 'calc(env(safe-area-inset-top) + 52px)' : 'calc(100% + 6px)',
          right: isMobile ? S[2] : 0,
          left: isMobile ? S[2] : 'auto',
          width: isMobile ? 'auto' : 380,
          maxHeight: '70vh', overflowY: 'auto',
          background: SHADCN.popover, color: SHADCN.popoverFg,
          border: `1px solid ${SHADCN.border}`, borderRadius: R.md,
          boxShadow: SHADCN.shadowXl,
          zIndex: 300, padding: S[3],
          display: 'flex', flexDirection: 'column', gap: S[3],
        }}>
          <div style={{ ...TYPO.eyebrow, color: SHADCN.mutedFg }}>Notifications</div>

          {isAdmin && <FormAnnonce onPublie={charger} />}

          {annonces.length === 0 && (
            <div style={{ ...TYPO.caption, color: SHADCN.mutedFg, textAlign: 'center', padding: S[4] }}>
              Rien pour le moment.
            </div>
          )}

          {annonces.map(a => {
            const meta = TYPE_META[a.type] || TYPE_META.info;
            const tone = meta.tone();
            return (
              <div key={a.id} style={{
                border: `1px solid ${SHADCN.border}`, borderRadius: R.sm,
                padding: S[3], display: 'flex', flexDirection: 'column', gap: S[1],
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: S[1] }}>
                  <span style={{
                    ...TYPO.caption, fontWeight: 600,
                    background: tone.bg, color: tone.fg, border: `1px solid ${tone.border}`,
                    borderRadius: R.pill, padding: `1px ${S[2]}px`,
                  }}>
                    {meta.label}
                  </span>
                  {a.audience === 'admin' && (
                    <span style={{ ...TYPO.caption, color: SHADCN.mutedFg, border: `1px solid ${SHADCN.border}`, borderRadius: R.pill, padding: `1px ${S[2]}px` }}>
                      Admins
                    </span>
                  )}
                  {a.epingle && <Pin size={11} style={{ color: SHADCN.mutedFg }} />}
                  <span style={{ ...TYPO.caption, color: SHADCN.mutedFg, marginLeft: 'auto' }}>{fmtDate(a.created_at)}</span>
                  {isAdmin && (
                    <button onClick={() => supprimer(a.id)} aria-label="Supprimer" style={{ background: 'none', border: 'none', color: SHADCN.mutedFg, cursor: 'pointer', padding: 0, display: 'flex' }}>
                      <X size={12} />
                    </button>
                  )}
                </div>
                <div style={{ ...TYPO.body, fontWeight: 600 }}>{a.titre}</div>
                <div style={{ ...TYPO.caption, color: SHADCN.mutedFg, whiteSpace: 'pre-wrap', lineHeight: 1.5 }}>{a.corps}</div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
